import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-who-uses';

/**
 * Who-uses quote card attributing a SignNow use case to a named persona.
 * Single green background card with full-width heading and author + role on top, then a two-column body text layout below.
 * Combines an attributed quote with side-by-side narrative columns, unlike typical-users type-1 which renders a checklist of audiences.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading describing who uses {keyword} and which documents or use cases it covers. 6-10 words.
 * @slot author - <me-text>
 * @example
 *   <sn-who-uses
 *     columns='[{"title":"Best-fit alternatives by user profile","text":"A co-performer release agreement is a legally binding document."},{"title":"Alternative perspectives","text":"A co-performer release agreement is a legally binding document."}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Who Uses / User Profiles</me-heading>
 *     <me-text slot="author"><strong>Mary P.</strong> Manager</me-text>
 *   </sn-who-uses>
 */
@Component({
  tag: 'sn-who-uses',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcUserProfilesQuote {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Two-column content blocks displayed below the heading.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Audience segment name or industry label (e.g. Real estate, Healthcare). 2-4 words.
   * @item.property {string} text - Paragraph describing how this audience segment uses {keyword}: their specific document workflows, key compliance requirements, and concrete benefits. You MUST write at least 40 words.
   * @item.required title
   * @item.required text
   * @item.content title text
   * @minItems 2
   * @maxItems 2
   */
  @Prop({ mutable: true }) columns?: { title?: string; text?: string }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `columns` prop.
   * @dynamic
   * @type {object}
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} author - Fallback author name (use slot="author" instead)
   * @property {string} role - Fallback author role (use slot="author" instead)
   * @property {string} title_left - Left column subtitle (use columns prop instead)
   * @property {string} text_left - Left column body text (use columns prop instead)
   * @property {string} title_right - Right column subtitle (use columns prop instead)
   * @property {string} text_right - Right column body text (use columns prop instead)
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        author?: string;
        role?: string;
        title_left?: string;
        text_left?: string;
        title_right?: string;
        text_right?: string;
        raw_html?: string;
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.columns = parseJsonProp(this.columns);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return (
        <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
          <div innerHTML={values.raw_html}></div>
        </me-section>
      );
    }

    const columns = Array.isArray(this.columns)
      ? this.columns
      : (() => {
          const cols: { title?: string; text?: string }[] = [];
          if (values?.title_left || values?.text_left) {
            cols.push({ title: values.title_left, text: values.text_left });
          }
          if (values?.title_right || values?.text_right) {
            cols.push({ title: values.title_right, text: values.text_right });
          }
          return cols;
        })();

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <div class={`${COMPONENT_NAME}__wrapper`}>
          <me-row direction="column" directionLg="column" yGap="xl" yGapLg="none">
            <me-column spanLg="12" class={`${COMPONENT_NAME}__header`}>
              <slot name="heading">
                {values?.title && (
                  <me-heading variant="4" tag="h4">
                    {values.title}
                  </me-heading>
                )}
              </slot>
              <slot name="author">
                {(values?.author || values?.role) && (
                  <me-text class={`${COMPONENT_NAME}__author`}>
                    {values.author && <strong>{values.author}</strong>}
                    {values.role && <span> {values.role}</span>}
                  </me-text>
                )}
              </slot>
            </me-column>
            {columns.length > 0 && (
              <me-column spanLg="12">
                <me-row yGap="xl" yGapLg="none" direction="column">
                  {columns.map((col, index) => (
                    <me-column spanLg="6">
                      <div class={`${COMPONENT_NAME}__column-block`}>
                        {col.title && (
                          <me-heading variant="6" tag="h3" class={`${COMPONENT_NAME}__column-title`}>
                            {col.title}
                          </me-heading>
                        )}
                        {col.text && <me-text class={`${COMPONENT_NAME}__column${index === columns.length - 1 ? ` ${COMPONENT_NAME}__column--last` : ''}`}>{col.text}</me-text>}
                      </div>
                    </me-column>
                  ))}
                </me-row>
              </me-column>
            )}
          </me-row>
        </div>
      </me-section>
    );
  }
}
